import {Box, Grid, GridItem, Hide, Show, VStack} from 'platform/foundation';
import {Lightbox, useLightbox, useLightboxActionsFactory} from 'platform/lightbox';

import {useState} from 'react';

import {dec, inc, isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {AdaptiveImageDc} from '../../components/AdaptiveImageDc/AdaptiveImageDc';
import {CarouselSlideshow} from '../../components/Carousel/components/CarouselSlideshow';
import {CarouselHeader} from '../../components/CarouselHeader/CarouselHeader';
import {Preview} from '../../components/Preview/Preview';
import {Section} from '../../components/Section/Section';
import {VideoWalkthroughDataBundle} from '../../hooks/useGetDataForVideoWalkthrough';
import i18n from '../../i18n/i18n';
import {getSlidesByPage} from '../../utils/getSlidesByPage';

interface VideoWalkthroughProps extends Omit<VideoWalkthroughDataBundle, 'sectionConfig'> {}

export function VideoWalkthrough(props: VideoWalkthroughProps) {
  const [photoDocumentationLightboxControls, {onOpenBySlide}] = useLightbox('videoWalkthrough');
  const actionsFactory = useLightboxActionsFactory();

  const [page, setPage] = useState(0);

  const onNext = (numberOfSlides: number) => {
    if (page === dec(numberOfSlides)) {
      setPage(0);
      return;
    }
    setPage(inc(page));
  };

  const onPrevious = (numberOfSlides: number) => {
    if (page === 0) {
      setPage(dec(numberOfSlides));
      return;
    }
    setPage(dec(page));
  };

  if (isNilOrEmpty(props.videoWalkthroughVideos)) {
    return null;
  }

  const slides = props.videoWalkthroughVideos ?? [];
  const {currentSlides, pages, slidesPerPage} = getSlidesByPage({slides, page});

  return (
    <>
      <Lightbox
        data-testid="videoWalkthrough"
        controls={photoDocumentationLightboxControls}
        videos={actionsFactory.addShareOnlyActions(props.videoWalkthroughVideos)}
      />
      <Section
        id="VIDEO_WALKTHROUGH"
        data-testid="videoWalkthrough"
        icon="AV/dc_play_arrow"
        heading={i18n.t('videoWalkthroughHeader')}
        content={
          <Box>
            <Hide onMobile>
              <VStack spacing={[3, 6, 6, 10]}>
                <CarouselHeader
                  data-testid="videoWalkthrough"
                  title={i18n.t('videos')}
                  slides={slides}
                  activeSlide={page}
                  onNextClick={() => onNext(pages)}
                  onPreviousClick={() => onPrevious(pages)}
                  numberOfImages={slidesPerPage}
                />
                <Grid columns={[1, 2, 4, 4]} spacing={[6, 6, 6, 10]}>
                  {currentSlides.map((slide, index) => {
                    const imageUrl = slide.coverImageUrl;
                    return (
                      <GridItem key={slide.id} span={1}>
                        <Show when={isNotNil(imageUrl)}>
                          <Preview
                            data-testid={`video-[${index}]`}
                            onClick={() => onOpenBySlide(slide, slides)}
                          >
                            <AdaptiveImageDc
                              data-testid={`video-[${index}]`}
                              fit="cover"
                              url={imageUrl ?? undefined}
                            />
                          </Preview>
                        </Show>
                        <Hide when={isNotNil(imageUrl)}>
                          <AdaptiveImageDc
                            fit="cover"
                            url={imageUrl ?? undefined}
                            data-testid={`video-[${index}]`}
                          />
                        </Hide>
                      </GridItem>
                    );
                  })}
                </Grid>
              </VStack>
            </Hide>
            <Show onMobile>
              <CarouselSlideshow
                slides={slides}
                hideLightbox
                lightboxId="videoWalkthrough"
                data-testid="videoWalkthrough"
              />
            </Show>
          </Box>
        }
      />
    </>
  );
}
